import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, User as UserIcon, ShieldCheck, KeyRound, ChevronDown, Settings, Menu } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

export const Header = ({ onOpenChangePassword, onToggleSidebar }) => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/login", { replace: true });
  };

  const initial = (user?.name || "?").charAt(0).toUpperCase();

  return (
    <header
      data-testid="app-header"
      className="border-b border-navy/10 bg-white"
    >
      <div className="pb-container px-4 sm:px-6 lg:px-10 h-[60px] sm:h-[64px] flex items-center justify-between gap-3">
        {/* Mobile Hamburger */}
        <button
          data-testid="header-mobile-menu"
          onClick={onToggleSidebar}
          className="md:hidden -ml-2 p-2 text-navy/75 hover:text-navy min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label="Menü öffnen"
        >
          <Menu size={22} strokeWidth={1.7} />
        </button>

        <Link
          to="/"
          data-testid="header-home-link"
          className="flex items-center gap-3 leading-none flex-1 min-w-0"
        >
          <span
            data-testid="header-logo-wordmark"
            className="font-display text-[16px] sm:text-[20px] lg:text-[22px] tracking-[-0.02em] leading-tight truncate"
            style={{
              color: "#C4922A",
              fontWeight: 700,
              fontFamily: "'Inter Tight', Inter, system-ui, sans-serif",
            }}
          >
            Newton Campus Garching
          </span>
        </Link>

        <div className="relative" ref={dropdownRef}>
          {user ? (
            <button
              data-testid="header-user-menu"
              onClick={() => setOpen((o) => !o)}
              className="flex items-center gap-2 hover:bg-ledger transition-colors px-2 py-1.5 border border-transparent hover:border-navy/10"
            >
              <span
                data-testid="header-user-initial"
                className="w-8 h-8 bg-navy text-white flex items-center justify-center font-display font-semibold text-sm"
                style={{ borderRadius: 0 }}
              >
                {initial}
              </span>
              <span className="text-xs uppercase tracking-widish text-navy/70 font-sans hidden sm:inline">
                {user.name.split(" ").pop()}
              </span>
              <ChevronDown size={12} className="text-navy/55"/>
            </button>
          ) : (
            <span className="text-sm text-navy/55 font-sans">Heinrichs</span>
          )}

          {open && user && (
            <div
              data-testid="header-user-dropdown"
              className="absolute right-0 mt-1 w-[260px] bg-white border border-navy/15 shadow-lg z-50"
            >
              <div className="px-4 py-3 border-b border-navy/10">
                <div className="text-xs uppercase tracking-widish text-navy/55 font-sans">Eingeloggt als</div>
                <div className="text-navy font-medium text-sm mt-0.5 truncate">{user.name}</div>
                <div className="text-xs text-navy/55 truncate">{user.email}</div>
              </div>
              <div className="py-1">
                <button
                  onClick={() => { setOpen(false); onOpenChangePassword?.(); }}
                  className="w-full text-left px-4 py-2 text-sm font-sans text-navy hover:bg-ledger flex items-center gap-2"
                  data-testid="menu-change-password"
                >
                  <KeyRound size={13}/> Passwort ändern
                </button>
                {user.rolle === "admin" && (
                  <>
                    <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widish text-navy/40 border-t border-navy/10 mt-1">
                      <ShieldCheck size={10} className="inline mr-1"/> Admin
                    </div>
                    <Link
                      to="/admin/users"
                      onClick={() => setOpen(false)}
                      data-testid="menu-admin-users"
                      className="block w-full text-left px-4 py-2 text-sm font-sans text-navy hover:bg-ledger flex items-center gap-2"
                    >
                      <UserIcon size={13}/> Benutzer verwalten
                    </Link>
                    <Link
                      to="/admin/aktivitaeten"
                      onClick={() => setOpen(false)}
                      data-testid="menu-admin-activity"
                      className="block w-full text-left px-4 py-2 text-sm font-sans text-navy hover:bg-ledger flex items-center gap-2"
                    >
                      <Settings size={13}/> Aktivitätenlog
                    </Link>
                    <Link
                      to="/admin/datenaudit"
                      onClick={() => setOpen(false)}
                      data-testid="menu-admin-datenaudit"
                      className="block w-full text-left px-4 py-2 text-sm font-sans text-navy hover:bg-ledger flex items-center gap-2"
                    >
                      <ShieldCheck size={13}/> Daten-Audit
                    </Link>
                  </>
                )}
                <div className="border-t border-navy/10 mt-1">
                  <button
                    onClick={handleLogout}
                    data-testid="menu-logout"
                    className="w-full text-left px-4 py-2 text-sm font-sans text-tot hover:bg-ledger flex items-center gap-2"
                  >
                    <LogOut size={13}/> Abmelden
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
